import { useRef } from 'react';
import { useState, useEffect } from 'react';
import { NotificationManager } from 'react-notifications';
import { Request } from '../../../classes/Request';
import { RequestNewVersion } from '../../../classes/RequestNewVersion';

const useUsersAndPermissionsListModal = ({
  setModalShowing,
  documentExpedientId,
  expedientId,
  fromPanel,
  lastDocument,
  onPermissionsUpdated,
}) => {
  const [isLoadingUsersPermissions, setIsLoadingUsersPermissions] = useState(false);
  const [usersPermissions, setUsersPermissions] = useState([]);
  const [usersPermissionsToSet, setUsersPermissionsToSet] = useState([]);
  const [usersPermissionsToSearch, setUsersPermissionsToSearch] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const electronicExpedientUsersPermissions = useRef([]);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const safeSetIsLoadingUsersPermissions = (value) => {
    if (isMountedRef.current) {
      setIsLoadingUsersPermissions(value);
    }
  };

  const safeSetUsersPermissions = (value) => {
    if (isMountedRef.current) {
      setUsersPermissions(value);
    }
  };

  const onUsersPermissionsToSearchInputChange = ({ target: { value } }) => {
    if (!value) {
      getDocumentUsersPermissions();
    }
    setUsersPermissionsToSearch(value);
  };

  const onSearchUserPermision = async (event) => {
    event.preventDefault();
    safeSetUsersPermissions([]);
    if (!usersPermissionsToSearch) {
      return;
    }
    try {
      safeSetIsLoadingUsersPermissions(true);
      const searchUsersPermissionsRequest = new Request(
        `${process.env.REACT_APP_URL_API_LARAVEL}/docuemnts_expedient/search_users/${usersPermissionsToSearch}/${documentExpedientId}`,
        'get',
        null,
        {}
      );
      const serachUsersPermissionsResponse =
        await searchUsersPermissionsRequest.executeRequest(false);

      if (
        serachUsersPermissionsResponse &&
        serachUsersPermissionsResponse.code &&
        Number(serachUsersPermissionsResponse.code === 200)
      ) {
        if (serachUsersPermissionsResponse.data.data.data.message === 'Usuario no encontrado') {
          safeSetUsersPermissions([]);
          return;
        }
        const _usersPermissions = getUpdatedPermissionsIfTheyHaveBeenChanged(
          serachUsersPermissionsResponse.data.data.data.users,
          usersPermissionsToSet
        );
        safeSetUsersPermissions(_usersPermissions);
      } else {
        throw new Error();
      }
    } catch (error) {
      console.error(error);
      NotificationManager.error('Ocurrió un error al intentar traer permisos');
    } finally {
      safeSetIsLoadingUsersPermissions(false);
    }
  };

  const getElectronicExpedientUsersPermissionsFormatted = (
    _electronicExpedientUsersPermissions
  ) => {
    const electronicExpedientUsersPermissionsFormatted = _electronicExpedientUsersPermissions.map(
      (singleElectronicExpedientUserPermission) => {
        return {
          ...singleElectronicExpedientUserPermission,
          isPermissionEnabled: false,
          fullname: singleElectronicExpedientUserPermission.name,
        };
      }
    );
    return electronicExpedientUsersPermissionsFormatted;
  };

  const showCollapseForUserPermissions = (userPermissions) => {
    const userPermissionsElement = document.getElementById(`userPermissions-${userPermissions.id}`);
    if (userPermissionsElement) {
      userPermissionsElement.classList.toggle('show');
    }
  };

  const getElectronicExpedientUsersPermissions = async () => {
    const requestGetElectronicExpedientUsersPermissions = new RequestNewVersion(
      `${process.env.REACT_APP_URL_API_LARAVEL}/electronic_expedients/get_user_permissions/${expedientId}`,
      'get',
      null,
      {}
    );

    try {
      safeSetIsLoadingUsersPermissions(true);
      const {
        code,
        response: { permissions = [] },
      } = await requestGetElectronicExpedientUsersPermissions.executeRequest();
      if (Number(code) === 200) {
        electronicExpedientUsersPermissions.current = permissions;
      }
    } catch (error) {
      throw new Error(error);
    } finally {
      safeSetIsLoadingUsersPermissions(false);
    }
  };

  const getDocumentUsersPermissions = async () => {
    safeSetUsersPermissions([]);
    try {
      safeSetIsLoadingUsersPermissions(true);
      const usersPermissionsRequest = new Request(
        `${process.env.REACT_APP_URL_API_LARAVEL}/docuemnts_expedient/get_user_permissions/${documentExpedientId}`,
        'get',
        null,
        {}
      );
      const usersPermissionsResponse = await usersPermissionsRequest.executeRequest(false);
      if (
        usersPermissionsResponse &&
        usersPermissionsResponse.code &&
        Number(usersPermissionsResponse.code === 200)
      ) {
        let data;
        if (Array.isArray(usersPermissionsResponse.data.data.data.users))
          data = usersPermissionsResponse.data.data.data.users;
        else data = Object.values(usersPermissionsResponse.data.data.data.users);
        const _usersPermissions = getUpdatedPermissionsIfTheyHaveBeenChanged(
          data,
          usersPermissionsToSet
        );
        const electronicExpedientUsersPermissionsFormatted =
          getElectronicExpedientUsersPermissionsFormatted(
            electronicExpedientUsersPermissions.current
          );
        const usersPermissionsMergedWithElectronicExpedientUsersPermissions =
          getUpdatedPermissionsIfTheyHaveBeenChanged(
            electronicExpedientUsersPermissionsFormatted,
            _usersPermissions
          );
        safeSetUsersPermissions(usersPermissionsMergedWithElectronicExpedientUsersPermissions);
      } else {
        throw new Error();
      }
    } catch (error) {
      console.error(error);
      NotificationManager.error('Ocurrió un error al intentar traer permisos');
    } finally {
      safeSetIsLoadingUsersPermissions(false);
    }
  };

  const getAllUsersPermissions = async () => {
    await getElectronicExpedientUsersPermissions();
    getDocumentUsersPermissions();
  };

  useEffect(() => {
    getAllUsersPermissions();
  }, []);

  const onPermissionToggled = ({ target: { checked, value } }) => {
    const userAlreadyExistedInUsersPermissionsToSet = usersPermissionsToSet.some(
      (user) => String(user.id) === String(value)
    );

    let _usersPermissionsToSet = usersPermissionsToSet;
    if (userAlreadyExistedInUsersPermissionsToSet) {
      const usersPermissionsToSetFiltered = usersPermissionsToSet.filter(
        (user) => String(user.id) !== String(value)
      );
      _usersPermissionsToSet = usersPermissionsToSetFiltered;
    }

    let _userPermissionToSet = usersPermissions.find((user) => String(user.id) === String(value));
    _userPermissionToSet = { ..._userPermissionToSet, isPermissionEnabled: checked };
    setUsersPermissionsToSet([..._usersPermissionsToSet, _userPermissionToSet]);
  };

  const onPostUsersPermissions = async () => {
    if (!usersPermissionsToSet.length) {
      setModalShowing(false);
      return;
    }
    safeSetIsLoadingUsersPermissions(true);
    let shouldModalBeClosed = false;

    const uersPermissionsToSetRequest = new Request(
      `${process.env.REACT_APP_URL_API_LARAVEL}/docuemnts_expedient/document_expedient_users_permissions/${documentExpedientId}`,
      'post',
      null,
      { users: usersPermissionsToSet }
    );

    try {
      const uersPermissionsToSetResponse = await uersPermissionsToSetRequest.executeRequest(false);
      if (String(uersPermissionsToSetResponse.code) === '200') {
        shouldModalBeClosed = true;
      } else {
        throw new Error();
      }
    } catch (error) {
      console.error(error);
      NotificationManager.error('Ocurrió un error al intentar guardar ');
    } finally {
      safeSetIsLoadingUsersPermissions(false);
      if (shouldModalBeClosed) {
        NotificationManager.success('Cambios realizados con éxito');

        const hasActivePermissions = usersPermissionsToSet.some((user) => user.isPermissionEnabled);

        if (fromPanel === true && hasActivePermissions && onPermissionsUpdated) {
          onPermissionsUpdated(documentExpedientId);
        } else {
          setModalShowing(false);
        }

        if (lastDocument === true) window.location.reload();
      }
    }
  };

  const getUpdatedPermissionsIfTheyHaveBeenChanged = (
    currentPermisssions,
    _usersPermissionsToSet
  ) => {
    if (!currentPermisssions.length && _usersPermissionsToSet.length) {
      return _usersPermissionsToSet;
    }

    const updatedPermisssions = [...currentPermisssions];
    currentPermisssions.forEach((currentPermisssionElement, currentPermisssionIndex) => {
      _usersPermissionsToSet.forEach((usersPermissionToSetElement) => {
        if (String(currentPermisssionElement.id) === String(usersPermissionToSetElement.id)) {
          updatedPermisssions[currentPermisssionIndex] = usersPermissionToSetElement;
        }
      });
    });

    return updatedPermisssions;
  };

  return {
    onPermissionToggled,
    isLoadingUsersPermissions,
    usersPermissions,
    onPostUsersPermissions,
    onUsersPermissionsToSearchInputChange,
    usersPermissionsToSearch,
    onSearchUserPermision,
    showConfirmModal,
    setShowConfirmModal,
    showCollapseForUserPermissions,
    usersPermissionsToSet,
  };
};

export default useUsersAndPermissionsListModal;
